import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { concat, interval, Observable, of, race, ReplaySubject } from 'rxjs';
import { delay, filter, mergeMap, pairwise, tap, takeUntil } from 'rxjs/operators';
import { WebSocketConnectionState } from 'types';
import { HWServiceConfig } from '../../service/hw-service-config';
import { BatteryInfo, BatteryState } from '../../service/hw-service-model';
import { SystemService, SystemServiceFactory } from '../../service/system.service';

const SYSTEM_SERVICE_RECONNECT_DELAY = 2000;

export enum BatteryStatus {
  Normal = 'Normal',
  Warning = 'Warning',
  Error = 'Error',
}

@Component({
  selector: 'i-battery-state-monitor',
  templateUrl: './battery-state-monitor.component.html',
  styleUrls: ['./battery-state-monitor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatteryStateMonitorComponent implements OnInit, OnDestroy {
  @Input() size: 'normal' | 'small' = 'normal';
  @Input() updateInterval = 1000;
  @Input() warningPercent = 20;
  @Input() errorPercent = 10;
  @Input() schedulePowerOff: Observable<number> | null = null;
  @Input() cancelPowerOff: Observable<void> | null = null;
  @Output() status = new EventEmitter<BatteryStatus>();

  public get isReportPresent(): boolean {
    return this.batteryReport !== null && (this.batteryReport.state === BatteryState.Absent || this.batteryReport.capacity >= 0);
  }

  public get isBatteryCharging(): boolean {
    return this.batteryReport !== null && this.batteryReport.state !== BatteryState.Discharging;
  }

  public get isBatteryCapacityNormal(): boolean {
    return this.batteryReport !== null && this.batteryReport.capacity > this.warningPercent;
  }

  public get isBatteryCapacityWarning(): boolean {
    return (
      this.batteryReport !== null && this.batteryReport.capacity > this.errorPercent && this.batteryReport.capacity <= this.warningPercent
    );
  }

  public get isBatteryCapacityError(): boolean {
    return this.batteryReport !== null && this.batteryReport.capacity <= this.errorPercent;
  }

  public get capacity(): number | null {
    if (this.batteryReport === null) {
      return null;
    }
    return this.batteryReport.state === BatteryState.Absent ? 0 : this.batteryReport.capacity;
  }

  private systemService: SystemService;
  private batteryReport: BatteryInfo | null = null;
  private dead = new ReplaySubject<void>(1);
  private isDead$ = this.dead.asObservable();

  constructor(
    private config: HWServiceConfig,
    private systemServiceFactory: SystemServiceFactory,
    private cdRef: ChangeDetectorRef,
    private zone: NgZone,
  ) {
    this.systemService = this.systemServiceFactory.create(config);
  }

  ngOnInit(): void {
    this.systemService.state$.pipe(takeUntil(this.isDead$), pairwise()).subscribe(
      ([prev, cur]) => {
        if (cur === WebSocketConnectionState.Closed && prev !== WebSocketConnectionState.Disconnecting) {
          this.setBatteryReport(null);
          this.reconnect();
        }
      },
      (e) => {
        this.setBatteryReport(null);
        this.reconnect();
      },
    );
    this.connect();
  }

  ngOnDestroy(): void {
    this.dead.next();
    this.dead.complete();
    this.systemService.disconnect().subscribe();
  }

  private setBatteryReport(report: BatteryInfo | null) {
    this.batteryReport = report;
    this.cdRef.detectChanges();
  }

  private getBatteryStatus$(): Observable<unknown> {
    return interval(this.updateInterval).pipe(
      mergeMap(() => this.systemService.getBatteryReport()),
      tap((r) => {
        this.setBatteryReport(r);
        if (!this.isReportPresent || this.isBatteryCharging || this.isBatteryCapacityNormal) {
          this.status.emit(BatteryStatus.Normal);
        } else if (this.isBatteryCapacityWarning) {
          this.status.emit(BatteryStatus.Warning);
        } else if (this.isBatteryCapacityError) {
          this.status.emit(BatteryStatus.Error);
        }
      }),
    );
  }

  private getPowerOffScheduler$(): Observable<unknown> {
    return (this.schedulePowerOff || of()).pipe(
      mergeMap((timeout) =>
        of(null).pipe(
          delay(timeout),
          takeUntil(this.cancelPowerOff || of()),
          tap(() => {
            const ss = this.systemServiceFactory.create(this.config);
            concat(ss.connect(), ss.shutdown()).subscribe();
          }),
        ),
      ),
    );
  }

  private connect(): void {
    this.zone.runOutsideAngular(() => {
      this.systemService.connect().subscribe(() => {
        this.attachBehavior(this.getBatteryStatus$());
        this.attachBehavior(this.getPowerOffScheduler$());
      });
    });
  }

  private reconnect(): void {
    of(null)
      .pipe(takeUntil(this.isDead$), delay(SYSTEM_SERVICE_RECONNECT_DELAY))
      .subscribe(() => this.connect());
  }

  private attachBehavior<T>(o: Observable<T>): void {
    o.pipe(
      takeUntil(race(this.systemService.state$.pipe(filter((x) => x === WebSocketConnectionState.Closed)), this.dead.asObservable())),
    ).subscribe();
  }
}
